import { useState, useCallback } from 'react';

/**
 * Custom hook for managing navigation sidebar state
 * 
 * @param {boolean} initialState - Initial sidebar open/closed state
 * @returns {Object} Sidebar state and controls
 * 
 * @example
 * const { isOpen, toggle, open, close } = useNavigation(false);
 */
export const useNavigation = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState);
  const [activeItemId, setActiveItemId] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Toggle sidebar open/close
  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Open sidebar
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Close sidebar
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Set active menu item
  const setActive = useCallback((itemId) => {
    setActiveItemId(itemId);
  }, []);

  // Toggle expanded state for a menu item
  const toggleExpanded = useCallback((itemId) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Expand a specific item
  const expand = useCallback((itemId) => {
    setExpandedItems((prev) => new Set(prev).add(itemId));
  }, []);

  // Collapse a specific item
  const collapse = useCallback((itemId) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  }, []);

  // Check if item is expanded
  const isExpanded = useCallback(
    (itemId) => expandedItems.has(itemId),
    [expandedItems]
  );

  return {
    isOpen,
    toggle,
    open,
    close,
    activeItemId,
    setActive,
    expandedItems,
    toggleExpanded,
    expand,
    collapse,
    isExpanded,
  };
};
